import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Plot from 'react-plotly.js';

import { selectRTFO, setStageSpikesAndComments } from '../../reducers/rtfoSlice';
import { selectUser } from '../../reducers/userSlice';
import { chartColors } from '../../helpers/chartColors';
import { useTheme } from '@mui/styles';
import { rtfoConversionFactors } from './rtfoConversionFactors';
// import savitzkyGolay from 'ml-savitzky-golay';

// const optionsLong = {
//   derivative: 0,
//   windowSize: 71,
//   polynomial: 1,
//   pad: 'pre',
//   padValue: 'replicate'
// };

export const RTFOPumpingChart = ({ 
  gridView, 
  showSpikeLines, 
  showLegend, 
  showComments,
  editMode, 
  events,
  stageComments, 
  setEditCommentVisible,
  setSelectedComment,
  preSandTimestamp,
  breakdownTimestamp,
  breakdownPressure,
  setBreakdownTimestamp,
  setBreakdownPressure,
  averageCalculationsRange,
  averagePumpingPressure,
  averagePumpingRate,
  stageTrim,
  setStageTrim
}) => {

  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector(selectUser);
  const darkMode = user?.value?.darkMode;
  
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;

  const currentStage = currentRTFO?.stageData?.[currentRTFO?.currentStageName];
  const stageData = currentStage?.coldboreData;
  const completionType = currentRTFO?.perfSchedule?.wells?.[currentRTFO?.currentStageName?.split('_')?.[1]?.toLowerCase()]?.completionType;
  const client = currentRTFO?.client;
  const units = currentRTFO?.settings?.units || 'metric';

  const data = useMemo(() => {
    if (!stageData || !stageData?.length > 0) return [];

    return getChartData();

    function getChartData() {
      try {

        const X = [];
        const YPressure = [];
        const YRate = [];
        const YProducedRate = [];
        const YPropConcSurf = [];
        const YPropConcBH = [];
        const YFR = [];
        const YFR2 = [];
        const YCrossLinker = [];
        const YGellant = [];
        const YTPRate = [];
        const XSpikes = [];
        const YPressureSpikes = [];
        const YRateSpikes = [];
        const XCommentsPressure = [];
        const XCommentsRate = [];
        const YCommentsPressure = [];
        const YCommentsRate = [];
        

        stageData?.forEach((d,i) => {
          if (d.timestamp > 0) {
            X.push(new Date(d.timestamp));
            YPressure.push((d.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            YRate.push((d.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1));
            YProducedRate.push(((currentRTFO?.name === '03-21B' ? d.clean * 0.1 : (d.producedRate || 0)) || 0) * (units === 'field' ? rtfoConversionFactors.producedRate : 1)); // TEMPORARILY ADDED FOR 03-21B PAD
            YPropConcSurf.push((d.proppant || 0) * (units === 'field' ? rtfoConversionFactors.proppant : 1));
            YPropConcBH.push((d.proppantBH || 0) * (units === 'field' ? rtfoConversionFactors.proppantBH : 1));
            YFR.push((d.fr || 0) * (units === 'field' ? 10 * rtfoConversionFactors.fr : 1)); // multiply by 10
            YFR2.push((d.fr2 || 0) * (units === 'field' ? rtfoConversionFactors.fr2 : 1));
            YCrossLinker.push((d.crossLinker || 0) * (units === 'field' ? rtfoConversionFactors.crossLinker : 1));
            YGellant.push((d.gellant || 0) * (units === 'field' ?  10 * rtfoConversionFactors.gellant : 1));
            YTPRate.push(d.tpRate >= 0 ? d.tpRate * (units === 'field' ? rtfoConversionFactors.tpRate : 1) : null);
          };
        });


        // let proppantData = stageData?.map(d => d.proppant || 0);
        // proppantData = savitzkyGolay(proppantData, 1, optionsLong);

        events?.perfClosure?.forEach((d,i) => {
          XSpikes.push(new Date(d.timestamp));
          YPressureSpikes.push((d.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
          YRateSpikes.push((d.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1));
        });

        if (showComments) {
          stageComments?.forEach((d,i) => {
            if (d?.channel === 'pressure') {
              XCommentsPressure.push(new Date(d.timestamp));
              YCommentsPressure.push((d?.pressure || 0) * (units === 'field' ? rtfoConversionFactors.pressure : 1));
            } else if (d?.channel === 'rate') {
              XCommentsRate.push(new Date(d.timestamp));
              YCommentsRate.push((d?.rate || 0) * (units === 'field' ? rtfoConversionFactors.rate : 1));
            };
          });
        };

        // add traces
        let treatingPressureTrace = {
          x: X,
          y: YPressure,
          mode: 'line',
          type: 'scattergl',
          xaxis: 'x1',
          yaxis: 'y1',
          name: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
          line: {
            width: 2,
            color: chartColors[2]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hovertemplate: `Treating Pressure: %{y:.2f} ${units === 'field' ? 'psi' : 'MPa'}<extra></extra>`,

          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YPressure.map(y => `Treating Pressure: ${y?.toFixed(2)} ${units === 'field' ? 'psi' : 'MPa'}`),
          connectgaps: false
        };
        let rateTrace = {
          x: X,
          y: YRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Slurry Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
          line: {
            width: 2,
            color: chartColors[3]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hovertemplate: `Slurry Rate: %{y:.2f} ${units === 'field' ? 'bbl/min' : 'm3/min'}<extra></extra>`,

          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YRate.map(y => `Slurry Rate: ${y?.toFixed(2)} ${units === 'field' ? 'bbl/min' : 'm3/min'}`),
          connectgaps: false,
        };
        let producedRateTrace = {
          x: X,
          y: YProducedRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y2',
          xaxis: 'x1',
          name: `Produced Rate ${units === 'field' ? 'bbl/min' : 'm3/min'}`,
          line: {
            width: 2,
            color: chartColors[7]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hovertext: YProducedRate.map(y => `Produced Rate: ${y?.toFixed(2)} ${units === 'field' ? 'bbl/min' : 'm3/min'}`),
        };
        let tpRateTrace = {
          x: X,
          y: YTPRate,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `TP Rate (${units === 'field' ? 'bbl/min' : 'L/min'})`,
          line: {
            width: 2,
            color: chartColors[0],
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hovertext: YTPRate.map(y => `TP Rate: ${y?.toFixed(2)} ${units === 'field' ? 'bbl/min' : 'L/min'}`),
        };
        let propConcSurfTrace = {
          x: X,
          y: YPropConcSurf,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `Propp. Surf. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[4]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
        };
        // let propConcSurfTraceSmooth = {
        //   x: X,
        //   y: proppantData,
        //   type: 'scattergl',
        //   mode: 'line',
        //   yaxis: 'y3',
        //   xaxis: 'x1',
        //   name: `Propp. Surf. SG (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        //   line: {
        //     width: 2,
        //     color: 'black'
        //   },
        //   hoverinfo: !showSpikeLines && 'none',
        //   hoverformat: '.2f',
        // };
        let propConcBHTrace = {
          x: X,
          y: YPropConcBH,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y3',
          xaxis: 'x1',
          name: `Propp. BH (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[6]
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YPropConcBH.map(y => `Propp. BH: ${y?.toFixed(2)} ${units === 'field' ? 'lb/gal' : 'kg/m3'}`),
        };
        let frConcTrace = {
          x: X,
          y: YFR,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `DFR Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dot'
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YFR.map(y => `DFR Conc.: ${y?.toFixed(2)} ${units === 'field' ? 'lb/gal' : 'kg/m3'}`),
        };
        let fr2ConcTrace = {
          x: X,
          y: YFR2,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `LFR Conc. (${units === 'field' ? 'gal/mgal' : 'L/m3'})`,
          line: {
            width: 2,
            color: chartColors[8],
            dash: 'dash'
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YFR2.map(y => `LFR Conc.: ${y?.toFixed(2)} ${units === 'field' ? 'gal/mgal' : 'L/m3'}`),
        };
        let crossLinkerConcTrace = {
          x: X,
          y: YCrossLinker,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `Cross-Linker Conc. (${units === 'field' ? 'gal/mgal' : 'L/m3'})`,
          line: {
            width: 2,
            color: chartColors[9],
            dash: 'dashdot'
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YCrossLinker.map(y => `Cross-Linker Conc.: ${y?.toFixed(2)} ${units === 'field' ? 'gal/mgal' : 'L/m3'}`),
        };
        let gellantConcTrace = {
          x: X,
          y: YGellant,
          type: 'scattergl',
          mode: 'line',
          yaxis: 'y4',
          xaxis: 'x1',
          name: `Gellant Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
          line: {
            width: 2,
            color: chartColors[10],
            dash: 'dashdot'
          },
          hoverinfo: !showSpikeLines && 'none',
          hoverformat: '.2f',
          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: YGellant.map(y => `Gellant Conc.: ${y?.toFixed(2)} ${units === 'field' ? 'lb/gal' : 'kg/m3'}`),
        };
        let pressureSpikesTrace = {
          x: XSpikes,
          y: YPressureSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y1',
          xaxis: 'x1',
          name: 'Rate Drop',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          hoverinfo: !showSpikeLines && 'none',

          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext: `Rate Drop`,
        };
        let rateSpikesTrace = {
          x: XSpikes,
          y: YRateSpikes,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate Drop',
          marker: {
            size: 12,
            color: chartColors[5],
            symbol: 'hourglass-open',
            line: {
              width: 2,
              color: chartColors[5]
            }
          },
          legendgroup: 'Rate Drop',
          showlegend: false,
          hoverinfo: !showSpikeLines && 'none',

          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext:`Rate Drop`
        };
        let commentsPressureTrace = {
          x: XCommentsPressure,
          y: YCommentsPressure,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y1',
          xaxis: 'x1',
          name: 'Pressure Comments',
          marker: {
            size: 12,
            color: '#fff',
            symbol: 'circle-open',
            line: {
              width: 2,
              color: '#fff',
            }
          },
          opacity: 0.75,
          legendgroup: 'Comments',
          hoverinfo: !showSpikeLines && 'none',

          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext:`Pressure Comment`,
          showlegend: false,
        };
        let commentsRateTrace = {
          x: XCommentsRate,
          y: YCommentsRate,
          type: 'scatter',
          mode: 'markers',
          yaxis: 'y2',
          xaxis: 'x1',
          name: 'Rate Comments',
          marker: {
            size: 12,
            color: '#fff',
            symbol: 'circle-open',
            line: {
              width: 2,
              color: '#fff',
            }
          },
          opacity: 0.75,
          showlegend: false,
          legendgroup: 'Comments',
          hoverinfo: !showSpikeLines && 'none',

          // hoverinfo: !showSpikeLines ? 'none' : 'text',
          // hovertext:`Rate Comment`,
        };

        let traces = [ 
          commentsPressureTrace,
          commentsRateTrace,
          treatingPressureTrace, 
          rateTrace,
          producedRateTrace,
          tpRateTrace,
          propConcSurfTrace,
          propConcBHTrace,
          frConcTrace,
          fr2ConcTrace,
          crossLinkerConcTrace,
          gellantConcTrace,
          pressureSpikesTrace,
          rateSpikesTrace
        ];

        if (!producedRateTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => !t.name.includes('Produced Rate'));
        };

        if (!frConcTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => !t.name.includes('DFR Conc.'));
        };

        if (!fr2ConcTrace?.y?.reduce((tot, curr) => tot + curr, 0) > 0) {
          traces = traces.filter(t => !t.name.includes('LFR Conc.'));
        };

        if (completionType !== 'coil') {
          traces = traces.filter(t => !t.name.includes('TP Rate'));
        };

        if (client !== 'artis' && client !== 'empire') {
          traces = traces.filter(t => !t.name.includes('Cross-Linker Conc.'));
        };

        if (client !== 'empire') {
          traces = traces.filter(t => !t.name.includes('Gellant Conc.'));
        };

        if (!showComments) {
          traces = traces.filter(t => t.name !== 'Comments');
        };


        // traces.push(propConcSurfTraceSmooth)

        return traces;
  
      } catch (error) {
        console.log(error);
        return [];
      };
    };
  }, [stageData, events?.perfClosure, showComments, units, showSpikeLines, completionType, client, currentRTFO?.name, stageComments]);

  const layout = {
    yaxis: { // Treating pressure y axis
      linecolor: chartColors[2],
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: chartColors[2],
      title: {
        text: `Treating Pressure (${units === 'field' ? 'psi' : 'MPa'})`,
        font: {
          size: 12
        },
      },
      anchor: 'x',
      range: units === 'field' ? [0, 15000] : [0, 100],
      tick0: 0,
      dtick: units === 'field' ? 3000 : 20,
      rangemode: 'nonnegative',
      side: 'left',
      hoverformat: '.2f'
    },
    yaxis2: { // Slurry rate y axis
      linecolor: chartColors[3],
      showgrid: false,
      color: chartColors[3],
      title: {
        text: `Slurry Rate and Produced Rate (${units === 'field' ? 'bbl/min' : 'm3/min'})`,
        font: {
          size: 12
        },
      },
      range: units === 'field' ? [0, 150] : [0, 25],
      rangemode: 'nonnegative',
      tick0: 0,
      dtick: units === 'field' ? 30 : 5,
      anchor: 'free',
      overlaying: 'y',
      side: 'left',
      position: 0,
      hoverformat: '.2f'
    },
    yaxis3: { // Prop conc surf y axis
      linecolor: chartColors[4],
      showgrid: false,
      color: chartColors[4],
      title: {
        text: completionType === 'coil' ? `Propp. Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'}) and TP Rate (${units === 'field' ? 'bbl/min' : 'L/min'})` : `Propp. Conc. (${units === 'field' ? 'lb/gal' : 'kg/m3'})`,
        font: {
          size: 12
        },
      },
      range: units === 'field' ? [0, 10] : [0, 1000],
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'x',
      hoverformat: '.2f'
    },
    yaxis4: { // FR conc y axis
      linecolor: chartColors[8],
      showgrid: false,
      color: chartColors[8],
      title: {
        text: (client === 'artis' || client === 'empire') ? 
        `FR, Cross-Linker, Gellant Conc. (${units === 'field' ? '10 x lb/gal or gal/mgal' : 'kg/m3 or L/m3'})` : 
        `FR Conc. (${units === 'field' ? '10 x lb/gal or gal/mgal' : 'kg/m3 or L/m3'})`,
        font: {
          size: 12
        },
      },
      range: units === 'field' ? [0, 100] : [0, 10],
      rangemode: 'nonnegative',
      overlaying: 'y',
      side: 'right',
      anchor: 'free',
      position: 1,
      uirevision: 'true',
      hoverformat: '.2f'
    },
    xaxis: { // Date x axis
      autorange: true,
      rangemode: 'nonnegative',
      title: {
        text: 'Date',
        font: {
          size: 12
        }
      },
      domain: [(gridView ? 0.1 : 0.05), (gridView ? 0.9 : 0.95)],
      linecolor: darkMode ? 'lightgrey' : 'black',
      gridcolor: darkMode ? 'grey' : 'lightgrey',
      color: darkMode ? 'lightgrey' : 'black',
      anchor: 'y',
      showspikes: true,
      spikethickness: 2,
      spikedash: "dot",
      spikecolor: "#999999",
      spikemode: "across",
      type: 'date',
    },
    hovermode: editMode === 'comment' ? "closest" : "x",
    hoverdistance: 20, // Distance to show hover label of data point
    spikedistance: 1000,
    autosize: true,
    showlegend: showLegend ? true : false,
    legend: {
      orientation: "h",
      yanchor: "top",
      font: {
        color: darkMode ? 'lightgrey' : 'grey',
        size: 10
      },
      y: (gridView ? -0.15 : -0.1),
    },
    annotations: [
      {
        text: "Pumping Schedule",
          font: {
          size: 14,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.15 : 1.075), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      {
        text: `Pad: ${currentRTFO?.name} | Well: ${currentStage?.wellName} | Stage: ${currentStage?.stageNumber || "No stage selected"}`,
          font: {
          size: 10,
           color: darkMode ? 'lightgrey' : 'black'
        },
        showarrow: false,
        align: 'center',
        x: 0.5, //position in x domain
        y: (gridView ? 1.09 : 1.035), //position in y domain
        xref: 'paper',
        yref: 'paper',
      },
      preSandTimestamp && { // pre sand line annotation
        x: new Date(preSandTimestamp),
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: 'Pre Sand',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        // arrowhead: 7,
        ax: 0,
        ay: -40
      },
      breakdownTimestamp && { // breakdown line annotation
        x: new Date(breakdownTimestamp),
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: 'Breakdown',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : '#333'},
        borderpad: 1,
        showarrow: false,
        // arrowhead: 7,
        ax: 0,
        ay: -40
      },
      (editMode === 'start' || editMode === 'end') && { // average P and Q annotation
        x: (averageCalculationsRange?.[0] + averageCalculationsRange?.[1]) / 2,
        y: 1,
        xref: 'x',
        yref: 'paper',
        text: `Pavg = ${
        (averagePumpingPressure * units === 'field' ? rtfoConversionFactors.pressure : 1)
        ?.toFixed(2)} ${units === 'field' ? 'psi' : 'MPa'}, Qavg = ${
        (averagePumpingRate * units === 'field' ? rtfoConversionFactors.rate : 1)?.toFixed(2)} ${units === 'field' ? 'bbl/min' : 'm3/min'}`,
        // bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: darkMode ? '#f5f5f5' : 'green', size: 10, weight: 'bold'},
        borderpad: 1,
        showarrow: false,
        // arrowhead: 7,
        ax: 0,
        ay: 0
      }
    ],
    shapes: [
      preSandTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(preSandTimestamp),
        y0: 0,
        x1: new Date(preSandTimestamp),
        y1: 0.99,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 1,
          dash: 'dot'
        }
      },
      breakdownTimestamp && {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(breakdownTimestamp),
        y0: 0,
        x1: new Date(breakdownTimestamp),
        y1: 0.99,
        line: {
          color: darkMode ? '#f5f5f5' : '#333',
          width: 1,
          dash: 'dot'
        }
      },
      (editMode === 'start' || editMode === 'end') && {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: averageCalculationsRange?.[0],
        y0: 0,
        x1: averageCalculationsRange?.[1],
        y1: 1,
        line: {
          width: 0,
        },
        fillcolor: 'green',
        opacity: 0.1
      },
      editMode === 'trim' && {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0: stageTrim?.start,
        y0: 0,
        x1: stageTrim?.end,
        y1: 1,
        line: {
          width: 0,
        },
        fillcolor: 'green',
        opacity: 0.1
      },

    ],
    uirevision: 'true',
    margin: {"t": 60, "l": 60, "r": 60, "b": 60},
    images: [
      {
        x: (gridView ? 0.05 : 0.035),
        y: (gridView ? 1.05 : 1.025),
        sizex: (gridView ? 0.1 : 0.05),
        sizey: 0.1,
        source: process.env.PUBLIC_URL + `/img/ssd_logomark_chart_${darkMode ? 'white' : 'primary'}.png`,
        xref: "page",
        yref: "y1 domain",
        xanchor: "right",
        yanchor: "bottom",
        opacity: 0.75
      }
    ],
    plot_bgcolor: "transparent",
    paper_bgcolor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')]
  };

  if ((events?.plugSlip || currentStage?.plugSlips)?.length > 0) {
    const newPlugSlipsLines = (events?.plugSlip || currentStage?.plugSlips)?.map((plugSlip, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(plugSlip?.timestamp),
        y0: 0,
        x1: new Date(plugSlip?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newPlugSlipsAnnotations = (events?.plugSlip || currentStage?.plugSlips)?.map((plugSlip, i) => {
      return {
        x: new Date(plugSlip.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Plug Slip',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newPlugSlipsLines);
    layout.annotations = layout.annotations.concat(newPlugSlipsAnnotations);
  };
  
  if (showComments) {
    const newAnnotations = stageComments?.map((c,i) => {
      return {
        text: c?.comment?.length <= 20 ? c?.comment : `${c?.comment?.substring(0, 20)}...`,
        font: {
          size: 14,
          color: darkMode ? '#eee' : '#333'
        },
        align: 'start',
        width: 150,
        x: new Date(c?.timestamp), // position in x domain
        y: c?.[c?.channel],
        xref: 'x1',
        yref: c?.channel === 'pressure' ? 'y1' : 'y2',
        showarrow: true,
        arrowhead: 6,
        arrowsize: 0.5,
        arrowcolor: darkMode ? '#eee' : '#333',
        ax: 0,
        ay: i % 2 === 0 ? -25 : 25
      }
    });
    layout.annotations = layout.annotations.concat(newAnnotations);
  };

  if (events?.sandoff?.length > 0) {
    const newSandoffLines = events?.sandoff.map((s, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(s?.timestamp),
        y0: 0,
        x1: new Date(s?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newSandoffAnnotations = events?.sandoff.map((s, i) => {
      return {
        x: new Date(s.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Sandoff',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newSandoffLines);
    layout.annotations = layout.annotations.concat(newSandoffAnnotations);
  };

  if (events?.isolationIssue?.length > 0) {
    const newIsolationIssueLines = events?.isolationIssue.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newIsolationIssueAnnotations = events?.isolationIssue.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Isolation Issue',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newIsolationIssueLines);
    layout.annotations = layout.annotations.concat(newIsolationIssueAnnotations);
  };

  if (events?.equipmentIssue?.length > 0) {
    const newEquipmentIssueLines = events?.equipmentIssue.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newEquipmentIssueAnnotations = events?.equipmentIssue.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Equipment Issue',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newEquipmentIssueLines);
    layout.annotations = layout.annotations.concat(newEquipmentIssueAnnotations);
  };

  if (events?.lostSuction?.length > 0) {
    const newLostSuctionLines = events?.lostSuction.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newLostSuctionAnnotations = events?.lostSuction.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Lost Suction',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLostSuctionLines);
    layout.annotations = layout.annotations.concat(newLostSuctionAnnotations);
  };

  if (events?.other?.length > 0) {
    const newOtherEventsLines = events?.other?.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newOtherEventsAnnotations = events?.other?.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: 'Other',
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newOtherEventsLines);
    layout.annotations = layout.annotations.concat(newOtherEventsAnnotations);
  };
  
  if (events?.fracfeed?.length > 0) {
    const newFracfeedLines = events?.fracfeed.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newFracfeedAnnotations = events?.fracfeed.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Fracfeed ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newFracfeedLines);
    layout.annotations = layout.annotations.concat(newFracfeedAnnotations);
  };

  if (events?.ballSeating?.length > 0) {
    const newLines = events?.ballSeating.map((event, i) => {
      return {
        type: 'line',
        xref: 'x',
        yref: 'paper',
        x0: new Date(event?.timestamp),
        y0: 0,
        x1: new Date(event?.timestamp),
        y1: 1,
        line: {
          color: 'red',
          width: 1,
          dash: 'dot'
        }
      };
    });
    const newAnnotations = events?.ballSeating.map((event, i) => {
      return {
        x: new Date(event.timestamp),
        y: 1.01,
        xref: 'x',
        yref: 'paper',
        text: `Ball Seating ${i + 1}`,
        textangle: '-90',
        bgcolor: (darkMode ? '#37474f' : '#fff'),
        font: {color: 'red'},
        borderpad: 1,
        showarrow: false,
        ax: 0,
        ay: -40
      };
    });

    layout.shapes = layout.shapes.concat(newLines);
    layout.annotations = layout.annotations.concat(newAnnotations);
  };

  const config = {
    displaylogo: false,
    toImageButtonOptions: {
      format: 'png', // one of png, svg, jpeg, webp
      filename: "RTFO-" + (new Date().toISOString().split('T')[0]),
      // filename: currentFBA?.name + "-" + (new Date().toISOString().split('T')[0]),
      height: 550,
      width: 960,
      scale: 1 // Multiply title/legend/axis/canvas sizes by this factor
    },
  };

  return (
    <Plot
      data={data}
      layout={layout}
      config={config}
      useResizeHandler={true}
      style={{width: '100%', height: '100%'}}
      onClick={(e) => handlePlotClick(e)}
    />
  );

  // FUNCTIONS
  function handlePlotClick(e) {
    try {

      if (editMode === 'comment') {
        const newPressureIndex = e.points?.find(point => point.data.name === 'Treating Pressure (MPa)' || point.data.name === 'Pressure (MPa)')?.pointIndex;
        const newRateIndex = e.points?.find(point => point.data.name === 'Slurry Rate (m3/min)' || point.data.name === 'Rate (m3/min)')?.pointIndex;
        let newComment = null;

        if (newPressureIndex >= 0) {
          newComment = {
            timestamp: stageData?.[newPressureIndex]?.timestamp, 
            pressure: stageData?.[newPressureIndex]?.pressure, 
            rate: stageData?.[newPressureIndex]?.rate, 
            channel: 'pressure',
            comment: ''
          };
        };
        if (newRateIndex >= 0 && !newComment) {
          newComment = {
            timestamp: stageData?.[newRateIndex]?.timestamp, 
            pressure: stageData?.[newRateIndex]?.pressure, 
            rate: stageData?.[newRateIndex]?.rate, 
            channel: 'rate',
            comment: ''
          };
        };

        if (!newComment) return;

        setSelectedComment(newComment);
        setEditCommentVisible(true);
        let newStageComments = null;
        if (!stageComments) {
          newStageComments = [newComment];
        } else {
          newStageComments = [...stageComments, newComment];
        };
        
        if (!newStageComments) return;
        
        newStageComments = newStageComments.sort((a, b) => a.timestamp - b.timestamp);

        dispatch(setStageSpikesAndComments({stageName: stageData?.name, stageSpikes: stageData?.spikes, stageComments: newStageComments}));
        
      } else if (editMode === 'breakdown') {
        // get a timestamp of the clicked point
        const newBreakdownTimestamp = new Date(e.points?.find(point => point?.data?.name === 'Treating Pressure (MPa)')?.x)?.getTime();
        
        // Check if breakdown line exists at clicked location and remove it if it does
        if (Math.abs(breakdownTimestamp - newBreakdownTimestamp) < 1000) {
          setBreakdownTimestamp(null);
          setBreakdownPressure(0);
        } else {
          const newBreakdownPressure = e.points?.find(point => point?.data?.name === 'Treating Pressure (MPa)')?.y;
          setBreakdownTimestamp(newBreakdownTimestamp);
          setBreakdownPressure(newBreakdownPressure);
        };
      } else if (editMode === 'trim') {
        // get a timestamp of the clicked point
        const newStartTimestamp = new Date(e.points?.find(point => point?.data?.name === 'Treating Pressure (MPa)')?.x)?.getTime();
        
        // update the edge closest to the clicked point
        if (Math.abs(stageTrim?.start - newStartTimestamp) < Math.abs(stageTrim?.end - newStartTimestamp) && newStartTimestamp < stageTrim?.end) { // update start
          setStageTrim(prev => ({...prev, start: newStartTimestamp}));
        } else if (newStartTimestamp > stageTrim?.start) { // update end
          setStageTrim(prev => ({...prev, end: newStartTimestamp}));
        };

      } else { // if regular click

        // check if a point exists at the clicked location
        const pressureTimestamp = new Date(e.points.find(point => point.data.name === 'Pressure Comments')?.x)?.getTime();
        const rateTimestamp = new Date(e.points.find(point => point.data.name === 'Rate Comments')?.x)?.getTime();
        let selectedComment = null;
        if (pressureTimestamp > 0) {
          selectedComment = stageComments?.find(comment => comment?.timestamp === pressureTimestamp && comment?.channel === 'pressure');
          if (selectedComment) {
            setSelectedComment(selectedComment);
            setEditCommentVisible(true);
          };
          return;
        } else if (rateTimestamp > 0 && !selectedComment) {
          selectedComment = stageComments?.find(comment => comment?.timestamp === rateTimestamp && comment?.channel === 'rate');
          if (selectedComment) {
            setSelectedComment(selectedComment);
            setEditCommentVisible(true);
          };
          return;
        };

      };
      
    } catch (error) {
      console.log(error);
    };
  };


};



export const RTFOPumpingChartMemo = React.memo(RTFOPumpingChart);